import { type MantineTheme, alpha } from '@mantine/core'

/**
 * Target model (see `.claude/docs/ui-surfaces.md`): every painted element is a
 * `surface` (opaque elevation level: 0 canvas, 1 raised, 2 overlay), a
 * `control` (inputs, default buttons: `control-*`) or an `item` (list/menu/nav
 * rows: `item-*`). Control and item tokens are level-independent — solid white
 * in light, translucent white in dark — so they read on any surface.
 *
 * Naming: `<category>-[variant]-[state]` (e.g. `control-border-hover`),
 * where state is one of (none, i.e. resting), `hover`, `focus`, `active`, or
 * `disabled`. `focus` is always sourced from Mantine's own
 * `--mantine-primary-color-filled` / `-light` chain, never a bespoke zinc
 * shade, so it tracks `primaryColor` and matches the native `:focus-visible`
 * outline Mantine already renders. When adding a category or state, wire it
 * into both the `light` and `dark` blocks below — never only one.
 *
 * Mantine's own CSS vars are a separate, flatter axis: a fixed list of
 * purpose names (`dimmed`, `placeholder`, `disabled`, `default`, `error`,
 * `anchor`, `bright`, ...) that its shipped components read directly, each
 * bundling whatever properties that purpose needs (e.g. `disabled` sets a
 * background, a text color, and a border via three sibling vars). These
 * aren't a 4th category to invent local names for — every real Mantine
 * purpose name gets aliased directly to a value from the categories above,
 * in the "Mantine global aliases" section of each `light`/`dark` block, so
 * unmodified Mantine components inherit this palette automatically. Check
 * `node_modules/@mantine/core/styles.css` for the authoritative list of
 * purpose names before adding a new alias.
 */
const surfaceTokens = (theme: MantineTheme) => ({
  light: {
    0: theme.colors.zinc[1],
    1: theme.white,
    2: theme.white,
  },
  dark: {
    0: theme.colors.zinc[10],
    1: theme.colors.zinc[9],
    2: theme.colors.zinc[8],
  },
})

const textTokens = (theme: MantineTheme) => ({
  light: {
    title: theme.colors.zinc[8],
    default: theme.colors.zinc[6],
    'default-hover': theme.colors.zinc[8],
    dimmed: theme.colors.zinc[5],
    placeholder: theme.colors.zinc[4],
    disabled: theme.colors.zinc[3],
    link: theme.colors.blue[6],
  },
  dark: {
    title: theme.colors.zinc[1],
    default: theme.colors.zinc[2],
    'default-hover': theme.colors.zinc[3],
    dimmed: theme.colors.zinc[4],
    placeholder: theme.colors.zinc[5],
    disabled: theme.colors.zinc[6],
    link: theme.colors.blue[4],
  },
})

const controlTokens = (theme: MantineTheme) => ({
  light: {
    bg: theme.white,
    'bg-hover': theme.colors.zinc[0],
    'bg-active': theme.colors.zinc[1],
    'bg-disabled': alpha(theme.colors.zinc[10], 0.04),
    border: alpha(theme.colors.zinc[10], 0.1),
    'border-hover': alpha(theme.colors.zinc[10], 0.32),
    'border-focus': 'var(--mantine-primary-color-filled)',
    'border-disabled': alpha(theme.colors.zinc[10], 0.08),
  },
  dark: {
    bg: alpha(theme.white, 0.04),
    'bg-hover': alpha(theme.white, 0.08),
    'bg-active': alpha(theme.white, 0.12),
    'bg-disabled': alpha(theme.white, 0.04),
    border: alpha(theme.white, 0.1),
    'border-hover': alpha(theme.white, 0.24),
    'border-focus': 'var(--mantine-primary-color-filled)',
    'border-disabled': alpha(theme.white, 0.06),
  },
})

const itemTokens = (theme: MantineTheme) => ({
  light: {
    'bg-hover': alpha(theme.colors.zinc[10], 0.05),
    'bg-selected': alpha(theme.colors.zinc[10], 0.08),
  },
  dark: {
    'bg-hover': alpha(theme.white, 0.06),
    'bg-selected': alpha(theme.white, 0.1),
  },
})

// Skeleton placeholders: translucent like items, so they read on every surface level
const skeletonTokens = (theme: MantineTheme) => ({
  light: {
    bg: alpha(theme.colors.zinc[10], 0.16),
  },
  dark: {
    bg: alpha(theme.white, 0.12),
  },
})

const elevationTokens = (theme: MantineTheme) => ({
  light: {
    border: theme.colors.zinc[1],
    shadow: theme.shadows.md,
    backdrop: alpha(theme.black, 0.7),
  },
  dark: {
    border: alpha(theme.white, 0.08),
    shadow: `0 8px 24px ${alpha(theme.black, 0.4)}`,
    backdrop: alpha(theme.black, 0.9),
  },
})

// Sticky bars over the canvas (page headers, filter bars, pagination): the
// canvas at 90% so scrolled content shows faintly through, paired with a
// `backdrop-filter: blur(12px)` at the consumer.
const STICKY_BAR_BG =
  'color-mix(in srgb, var(--mantine-color-surface-0) 90%, transparent)'

const errorTokens = (theme: MantineTheme) => ({
  light: theme.colors.red[5],
  dark: theme.colors.red[5],
})

// Mantine derives the dark `outline` variant from shade `primaryShade - 4`
// (shade 1 with our `primaryShade: 5`), which is washed out. Point it at shade
// 4 for every palette; the palettes themselves stay untouched.
const outlineVariantTokens = (theme: MantineTheme) =>
  Object.fromEntries(
    Object.entries(theme.colors).flatMap(([name, shades]) => [
      [`--mantine-color-${name}-outline`, `var(--mantine-color-${name}-4)`],
      [`--mantine-color-${name}-outline-hover`, alpha(shades[4], 0.05)],
    ]),
  )

// Mantine 9 made `--mantine-color-{c}-light` / `-light-hover` solid shades,
// which show as opaque patches on raised and overlay surfaces. Make them
// translucent tints of the filled color again (Mantine 8 behaviour) so
// `light` and `subtle` variants read on any surface level. Every component
// reading these vars (Button, Badge, ActionIcon, Alert, NavLink, ...) follows.
const lightVariantTokens = (theme: MantineTheme) =>
  Object.fromEntries(
    Object.keys(theme.colors).flatMap((name) => [
      [
        `--mantine-color-${name}-light`,
        `color-mix(in srgb, var(--mantine-color-${name}-filled) 12%, transparent)`,
      ],
      [
        `--mantine-color-${name}-light-hover`,
        `color-mix(in srgb, var(--mantine-color-${name}-filled) 20%, transparent)`,
      ],
    ]),
  )

const cssVariablesResolver = (theme: MantineTheme) => {
  const surface = surfaceTokens(theme)
  const text = textTokens(theme)
  const error = errorTokens(theme)
  const control = controlTokens(theme)
  const item = itemTokens(theme)
  const skeleton = skeletonTokens(theme)
  const elevation = elevationTokens(theme)

  return {
    variables: {},
    light: {
      // Surfaces
      '--mantine-color-surface-0': surface.light[0],
      '--mantine-color-surface-1': surface.light[1],
      '--mantine-color-surface-2': surface.light[2],
      '--mantine-color-border-surface': elevation.light.border,
      '--mantine-shadow-overlay': elevation.light.shadow,
      '--mantine-color-backdrop': elevation.light.backdrop,
      '--mantine-color-surface-0-sticky': STICKY_BAR_BG,

      // Control colors
      '--mantine-color-control-bg': control.light.bg,
      '--mantine-color-control-bg-hover': control.light['bg-hover'],
      '--mantine-color-control-bg-active': control.light['bg-active'],
      '--mantine-color-control-bg-disabled': control.light['bg-disabled'],
      '--mantine-color-control-border': control.light.border,
      '--mantine-color-control-border-hover': control.light['border-hover'],
      '--mantine-color-control-border-focus': control.light['border-focus'],
      '--mantine-color-control-border-disabled': control.light['border-disabled'],

      // Item colors
      '--mantine-color-item-bg-hover': item.light['bg-hover'],
      '--mantine-color-item-bg-selected': item.light['bg-selected'],

      // Skeleton
      '--mantine-color-skeleton-bg': skeleton.light.bg,

      // Canvas
      '--mantine-color-body': surface.light[0],

      // Text colors
      '--mantine-color-text': text.light.title,
      '--mantine-color-text-title': text.light.title,
      '--mantine-color-text-default': text.light.default,
      '--mantine-color-text-default-hover': text.light['default-hover'],
      '--mantine-color-text-dimmed': text.light.dimmed,
      '--mantine-color-text-placeholder': text.light.placeholder,
      '--mantine-color-text-disabled': text.light.disabled,
      '--mantine-color-text-link': text.light.link,

      // Mantine global aliases
      '--mantine-color-dimmed': text.light.dimmed,
      '--mantine-color-placeholder': text.light.placeholder,
      '--mantine-color-bright': text.light.title,
      '--mantine-color-anchor': text.light.link,
      '--mantine-color-error': error.light,
      '--mantine-color-default': control.light.bg,
      '--mantine-color-default-hover': control.light['bg-hover'],
      '--mantine-color-default-color': text.light.default,
      '--mantine-color-default-border': control.light.border,
      '--mantine-color-disabled': control.light['bg-disabled'],
      '--mantine-color-disabled-color': text.light.disabled,
      '--mantine-color-disabled-border': control.light['border-disabled'],

      ...lightVariantTokens(theme),
    },
    dark: {
      // Surfaces
      '--mantine-color-surface-0': surface.dark[0],
      '--mantine-color-surface-1': surface.dark[1],
      '--mantine-color-surface-2': surface.dark[2],
      '--mantine-color-border-surface': elevation.dark.border,
      '--mantine-shadow-overlay': elevation.dark.shadow,
      '--mantine-color-backdrop': elevation.dark.backdrop,
      '--mantine-color-surface-0-sticky': STICKY_BAR_BG,

      // Control colors
      '--mantine-color-control-bg': control.dark.bg,
      '--mantine-color-control-bg-hover': control.dark['bg-hover'],
      '--mantine-color-control-bg-active': control.dark['bg-active'],
      '--mantine-color-control-bg-disabled': control.dark['bg-disabled'],
      '--mantine-color-control-border': control.dark.border,
      '--mantine-color-control-border-hover': control.dark['border-hover'],
      '--mantine-color-control-border-focus': control.dark['border-focus'],
      '--mantine-color-control-border-disabled': control.dark['border-disabled'],

      // Item colors
      '--mantine-color-item-bg-hover': item.dark['bg-hover'],
      '--mantine-color-item-bg-selected': item.dark['bg-selected'],

      // Skeleton
      '--mantine-color-skeleton-bg': skeleton.dark.bg,

      // Canvas
      '--mantine-color-body': surface.dark[0],

      // Text colors
      '--mantine-color-text': text.dark.default,
      '--mantine-color-text-title': text.dark.title,
      '--mantine-color-text-default': text.dark.default,
      '--mantine-color-text-default-hover': text.dark['default-hover'],
      '--mantine-color-text-dimmed': text.dark.dimmed,
      '--mantine-color-text-placeholder': text.dark.placeholder,
      '--mantine-color-text-disabled': text.dark.disabled,
      '--mantine-color-text-link': text.dark.link,

      // Mantine global aliases
      '--mantine-color-dimmed': text.dark.dimmed,
      '--mantine-color-placeholder': text.dark.placeholder,
      '--mantine-color-bright': text.dark.title,
      '--mantine-color-anchor': text.dark.link,
      '--mantine-color-error': error.dark,
      '--mantine-color-default': control.dark.bg,
      '--mantine-color-default-hover': control.dark['bg-hover'],
      '--mantine-color-default-color': text.dark.default,
      '--mantine-color-default-border': control.dark.border,
      '--mantine-color-disabled': control.dark['bg-disabled'],
      '--mantine-color-disabled-color': text.dark.disabled,
      '--mantine-color-disabled-border': control.dark['border-disabled'],

      ...lightVariantTokens(theme),
      ...outlineVariantTokens(theme),
    },
  }
}

export default cssVariablesResolver
